import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import GithubSlugger from 'github-slugger';
import { Link } from 'react-router-dom';
import PageNavigation from './PageNavigation';
import TableOfContents from './TableOfContents';
import { getPostBySlug, fetchPostContent } from '../posts/posts';
import './BlogPost.css';

// Helper function to extract headings from markdown content
function extractHeadings(content) {
  if (!content) return [];
  
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const headings = [];
  const slugger = new GithubSlugger();
  let match;
  
  while ((match = headingRegex.exec(content)) !== null) {
    const depth = match[1].length;
    const text = match[2].trim();
    // Use github-slugger to generate IDs that match rehype-slug
    const id = slugger.slug(text);
    
    headings.push({ depth, text, id });
  }
  
  // Reset slugger for next extraction
  slugger.reset();
  
  return headings;
}

function BlogPost({ slug }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const post = getPostBySlug(slug);
  
  const headings = useMemo(() => extractHeadings(content), [content]);

  useEffect(() => {
    if (!post) {
      setError('Post not found');
      setLoading(false);
      return;
    }

    async function loadPost() {
      try {
        const postContent = await fetchPostContent(post.filename);
        if (postContent) {
          setContent(postContent);
        } else {
          setError('Failed to load post content');
        }
      } catch (err) {
        setError('Error loading post');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    loadPost();
  }, [post]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <p className="page-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="page-container">
        <div className="page-content">
          <p className="page-text">{error || 'Post not found'}</p>
          <Link to="/blog" className="back-link">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageNavigation />
      <div className="page-content blog-post-wrapper">
        <article className="blog-post">
          <header className="blog-post-header">
            <h1 className="blog-post-title">{post.title}</h1>
          </header>
          <div className="blog-post-content">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]} 
              rehypePlugins={[
                rehypeRaw,
                rehypeSlug,
                [rehypeAutolinkHeadings, { behavior: 'wrap', properties: { className: ['heading-link'] } }]
              ]}
              components={{
                iframe: ({ node, title, ...props }) => (
                  <iframe 
                    {...props} 
                    title={title || 'Embedded content'}
                    style={{ maxWidth: '100%', margin: '20px 0' }} 
                  />
                )
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
          <div className="blog-post-footer">
            <time className="blog-post-date" dateTime={post.date}>
              {(() => {
                const [year, month, day] = post.date.split('-');
                return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
              })()}
            </time>
            <Link to="/blog" className="back-link">← Back to Blog</Link>
          </div>
        </article>
        <TableOfContents headings={headings} />
      </div>
    </div>
  );
}

export default BlogPost;

